import React, { useState } from 'react'
import ValidationMessage from './ValidationMessage'
import ShowPassword from '@Images/show-password.svg'
import HidePassword from '@Images/hide-password.svg'
import classnames from 'classnames'

import './AuthInput.scss'

interface AuthInputProps {
  readonly id: string
  readonly label: string
  readonly type?: string
  readonly placeholder: string
  readonly value: string
  readonly autoComplete: string
  readonly isPassword?: boolean
  readonly error?: string
  readonly showCharCount?: boolean
  readonly maxLength?: number
  readonly 'data-testid': string
  onChange(val: string): void
  onBlur?(): void
}

const AuthInput: React.FC<AuthInputProps> = ({ id, label, type = 'text', placeholder, value, autoComplete, onChange, isPassword, error, showCharCount, maxLength, onBlur, 'data-testid': dataTestId }) => {
  const [focused, setFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const inputType = isPassword ? (showPassword ? 'text' : 'password') : type
  const hasError = !!error

  const hasLengthLimit = maxLength
  const isOverLimit = maxLength ? value.length > maxLength : false
  const charCountClassName = 'AuthInput__char-count' + (isOverLimit ? ' AuthInput__char-count-warning' : '')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  const handleFocus = () => {
    setFocused(true)
  }

  const handleBlur = () => {
    setFocused(false)
    if (onBlur) {
      onBlur()
    }
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="AuthInput">
      <label className="AuthInput__label" htmlFor={id}>
        {label}
        {showCharCount && hasLengthLimit && (
          <span className={charCountClassName}>{value.length}/{maxLength}</span>
        )}
      </label>
      <div
        className={classnames(
          'AuthInput__wrapper',
          { 'AuthInput__wrapper-focused': focused},
          { 'AuthInput__wrapper-error': hasError}
        )}
      >
        <input
          id={id}
          name={id}
          className="AuthInput__input"
          type={inputType}
          placeholder={placeholder}
          value={value}
          autoComplete={autoComplete}
          data-testid={dataTestId}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {isPassword && (
          <button
            type="button"
            className="AuthInput__eye-button"
            onClick={toggleShowPassword}
            tabIndex={-1}
            data-testid={`${dataTestId}-visibility-toggle`}
          >
            {showPassword ? (
              <ShowPassword />
            ) : (
              <HidePassword />
            )}
          </button>
        )}
      </div>
      {hasError && <ValidationMessage message={error} />}
    </div>
  )
}

export default AuthInput
